// @flow
import React, { memo } from "react";
import cx from "classnames";
import dayjs from "dayjs";
import t from "common/i18n";
import RadioGroup from "root/components/RadioGroup";
import SvgIcon from "root/components/SvgIcon";
import type { MEDIA_TYPE, Subscription } from "../../data/types";
import SVG_ICONS from "~/assets/icons/icons";

type Props = {
    className: string,
    subscriptions: Subscription[],
    plan: string,
    mediaType: MEDIA_TYPE,
    changePurchaseDetails: Function,
};

const ManageLink = () => <a className="b ml1 w-40 pointer-events-all"
	href="https://premier.shutterstock.com/account/plans/"
	target="_blank"
	rel="noopener noreferrer">
	{t("shutterstock.purchase_page.plans.manage_plans")}
</a>;

const Tip = ({ text }) => <span
	className="o-50 mr3 pr3 w-25 tr br1 br--right pt2 pb2"
	style={{
		background: "rgba(101, 157, 209, 0.45)",
	}}>
	{text}
</span>;

const formatTimeLeft = (end: Date) => {
	const now = dayjs();

	const inYears = dayjs(end).diff(now, "years", true).toFixed(1);
	const inDays = dayjs(end).diff(now, "days");

	return inDays > 0 && (inDays > 365 ?
		`${inYears} ${t("shutterstock.purchase_page.plans.years")}` :
		`${inDays} ${t("shutterstock.purchase_page.plans.days")}`);
};

const formatNumber = (number: number) => {
	let formatted = number;

	if (number > 999) {
		formatted = number / 1000;

		formatted = Number.isInteger(formatted)
			? `${formatted}k`
			: `${formatted.toFixed(1)}k`;
	}

	return formatted;
};

const DisabledSubOption = ({ expired }) => (expired
	? <Tip text={t("shutterstock.purchase_page.plans.expired")} />
	: <Tip text={t("shutterstock.purchase_page.plans.exceeded")} />);

const getPlanPrice = (plans: Subscription[], id: string) =>
		plans
		.find((p) => p.id === id)
		.price_per_download?.local_amount;

const Plans = (props: Props) => {
	const getSubscriptionOptions = () => props.subscriptions.map((sub: Subscription) => {
		const
			validSubTime = formatTimeLeft(sub.expiration_time);
		const exceeded = validSubTime && sub.allotment && sub.allotment.downloads_left === 0;
		const disabled = exceeded || !validSubTime;

		return {
			value: sub.id,
			label: (
				<div className={cx("w-100 flex justify-between ttc items-center",
					{ compensateForPadding: disabled })}>
					<span className={cx("b flex-grow-1 truncate tl br1 br--left pt2 pb2", {
						"o-50 pl3 blueBg": disabled,
					})}>
						{sub.description}
					</span>
					{disabled ? <>
						<DisabledSubOption expired={!validSubTime}/>
						<ManageLink/>
					</>
						: <>
							<span className="o-50 mr1 ml1 tr w-25">
								{validSubTime && <span>
									{formatNumber(sub.allotment.downloads_left)} /
									{formatNumber(sub.allotment.downloads_limit)}
								</span>}
							</span>
							<span className="o-50 mr1 ml1 tr w-30">
								{t("shutterstock.purchase_page.time_left", { time: validSubTime })}
							</span>
						</>}

				</div>),
			disable: disabled,
		};
	});


	return <li className={props.className}>
		<h4 className={cx("bg-source-bg-c", "b")}>{t("shutterstock.purchase_page.plans.label")}</h4>
		{ props.plan
			? <RadioGroup
				options={getSubscriptionOptions()}
				onChange={(plan) => {
					props.changePurchaseDetails({ plan, price: getPlanPrice(props.subscriptions, plan) });
				}}
				className="w-100"
				value={props.plan}
			/>
			: <>
				<div className="flex items-center justify-start pb-3">
					<SvgIcon path={SVG_ICONS.WARNING} size="sm" colorName="menu-icons"/>
					<span className="pl-2 f-5">{t("shutterstock.purchase_page.plans.no_plan")} {props.mediaType}</span>
				</div>
				<ManageLink/>
			</>}
	</li>;
};

export default memo<Props>(Plans);
